import React from 'react';
import {
  ShieldCheck,
  FileSearch,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Clock,
  CreditCard,
  Calendar,
  MapPin,
  Phone,
  Package,
  DollarSign,
} from 'lucide-react';
import { SectionCard, InfoRow, TabLoading, TabError, TabEmpty, StatusBadge, getGenericStatusVariant } from './shared';
import { useUserIdentityVerification, useUserBackgroundVerification } from '../../hooks/useUserTabs';
import { cn, formatDateTime, parseDateOnly } from '../../utils/helpers';

interface VerificationTabProps {
  userId: number;
}

const VerificationTab: React.FC<VerificationTabProps> = ({ userId }) => {
  const identity = useUserIdentityVerification(userId, true);
  const background = useUserBackgroundVerification(userId, true);

  const bothLoading = identity.loading && background.loading;
  const bothError = identity.error && background.error;

  if (bothLoading) return <TabLoading />;
  if (bothError) return <TabError message={identity.error || background.error || 'Failed to load verification data'} />;

  return (
    <div className="space-y-5">
      {/* Identity Verification (Stripe) */}
      <IdentityVerificationSection
        data={identity.data}
        loading={identity.loading}
        error={identity.error}
      />

      {/* Background Check (Checkr) */}
      <BackgroundCheckSection
        data={background.data}
        loading={background.loading}
        error={background.error}
      />
    </div>
  );
};

// ============================================================================
// Identity Verification Section
// ============================================================================

interface IdentityVerificationSectionProps {
  data: import('../../types/userTabs').IdentityVerificationData | null;
  loading: boolean;
  error: string | null;
}

const IdentityVerificationSection: React.FC<IdentityVerificationSectionProps> = ({ data, loading, error }) => {
  if (loading) {
    return (
      <SectionCard title="Identity Verification (Stripe)" icon={ShieldCheck}>
        <div className="flex items-center justify-center py-10">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          <span className="ml-2.5 text-sm text-muted-foreground">Loading...</span>
        </div>
      </SectionCard>
    );
  }

  if (error) {
    const is404 = error.includes('404') || error.toLowerCase().includes('not yet available');
    return (
      <SectionCard title="Identity Verification (Stripe)" icon={ShieldCheck}>
        <div className="flex flex-col items-center justify-center py-10 gap-2">
          {is404 ? (
            <>
              <ShieldCheck className="h-6 w-6 text-muted-foreground/40" />
              <p className="text-sm text-muted-foreground">Endpoint not yet available on the backend</p>
            </>
          ) : (
            <>
              <XCircle className="h-6 w-6 text-red-400" />
              <p className="text-sm text-muted-foreground">{error}</p>
            </>
          )}
        </div>
      </SectionCard>
    );
  }

  if (!data) {
    return (
      <SectionCard title="Identity Verification (Stripe)" icon={ShieldCheck}>
        <TabEmpty message="No identity verification data" />
      </SectionCard>
    );
  }

  const { userProfile, latestVerification, verificationHistory } = data;
  const mismatches = data.mismatches ?? { name: false, dateOfBirth: false, email: false, phone: false };
  const hasMismatches = mismatches.name || mismatches.dateOfBirth || mismatches.email || mismatches.phone;

  const addressParts = [userProfile.address, userProfile.city, userProfile.state, userProfile.zipCode, userProfile.country].filter(Boolean);
  const fullAddress = addressParts.length > 0 ? addressParts.join(', ') : null;

  return (
    <SectionCard
      title="Identity Verification (Stripe)"
      icon={ShieldCheck}
      subtitle={latestVerification ? `Status: ${latestVerification.status}` : 'No verification on file'}
      actions={
        latestVerification ? (
          <StatusBadge
            status={latestVerification.status}
            variant={getGenericStatusVariant(latestVerification.status)}
          />
        ) : undefined
      }
    >
      <div className="space-y-5">
        {/* User Profile Info */}
        <div className="space-y-0">
          <InfoRow label="Name" icon={ShieldCheck} value={userProfile.name} />
          <InfoRow label="Email" value={userProfile.email} />
          <InfoRow label="Phone" icon={Phone} value={userProfile.phoneNumber} />
          <InfoRow
            label="Date of Birth"
            icon={Calendar}
            value={
              userProfile.dateOfBirth
                ? parseDateOnly(userProfile.dateOfBirth).toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric',
                  })
                : null
            }
          />
          {fullAddress && (
            <InfoRow label="Address" icon={MapPin} value={fullAddress} />
          )}
        </div>

        {/* Latest Verification Details */}
        {latestVerification ? (
          <div className="space-y-0">
            <div className="mb-2">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Latest Verification
              </p>
            </div>
            <InfoRow
              label="Verified Name"
              icon={ShieldCheck}
              value={
                <span className={cn(mismatches.name && 'text-amber-400')}>
                  {[latestVerification.verifiedFirstName, latestVerification.verifiedLastName]
                    .filter(Boolean)
                    .join(' ') || 'N/A'}
                  {mismatches.name && (
                    <span className="ml-2 inline-flex items-center gap-1 text-[10px] text-amber-400">
                      <AlertTriangle className="h-3 w-3" />
                      Mismatch with profile
                    </span>
                  )}
                </span>
              }
            />
            <InfoRow
              label="Verified Date of Birth"
              icon={Calendar}
              value={
                <span className={cn(mismatches.dateOfBirth && 'text-amber-400')}>
                  {latestVerification.verifiedDateOfBirth
                    ? parseDateOnly(latestVerification.verifiedDateOfBirth).toLocaleDateString('en-US', {
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric',
                      })
                    : 'N/A'}
                  {mismatches.dateOfBirth && (
                    <span className="ml-2 inline-flex items-center gap-1 text-[10px] text-amber-400">
                      <AlertTriangle className="h-3 w-3" />
                      Mismatch
                    </span>
                  )}
                </span>
              }
            />
            <InfoRow
              label="Verified Email"
              value={
                <span className={cn(mismatches.email && 'text-amber-400')}>
                  {latestVerification.verifiedEmail || 'N/A'}
                  {mismatches.email && (
                    <span className="ml-2 inline-flex items-center gap-1 text-[10px] text-amber-400">
                      <AlertTriangle className="h-3 w-3" />
                      Mismatch
                    </span>
                  )}
                </span>
              }
            />
            <InfoRow
              label="Stripe Verification ID"
              value={latestVerification.stripeVerificationId}
              mono
            />
            <InfoRow
              label="Submitted"
              icon={Clock}
              value={latestVerification.createdAt ? formatDateTime(latestVerification.createdAt) : 'N/A'}
            />
            <InfoRow
              label="Last Updated"
              icon={Clock}
              value={latestVerification.updatedAt ? formatDateTime(latestVerification.updatedAt) : 'N/A'}
            />
          </div>
        ) : (
          <div className="rounded-lg border border-border/30 bg-muted/5 p-4 text-center">
            <p className="text-sm text-muted-foreground">No identity verification submitted</p>
          </div>
        )}

        {/* Mismatch Summary */}
        {hasMismatches && (
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3.5">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="h-4 w-4 text-amber-400" />
              <p className="text-xs font-semibold text-amber-400">Data Mismatches Detected</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {mismatches.name && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-500/10 text-amber-400 border border-amber-500/20">
                  Name
                </span>
              )}
              {mismatches.dateOfBirth && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-500/10 text-amber-400 border border-amber-500/20">
                  Date of Birth
                </span>
              )}
              {mismatches.email && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-500/10 text-amber-400 border border-amber-500/20">
                  Email
                </span>
              )}
              {mismatches.phone && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-500/10 text-amber-400 border border-amber-500/20">
                  Phone
                </span>
              )}
            </div>
            <p className="text-[11px] text-muted-foreground mt-2">
              Profile: {userProfile.name || 'N/A'} &middot; {userProfile.email || 'N/A'}
            </p>
          </div>
        )}

        {/* Verification History */}
        {verificationHistory.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
              Verification History
            </p>
            <div className="space-y-0">
              {verificationHistory.map((entry, index) => (
                <div
                  key={entry.id}
                  className="flex items-center gap-3 py-2.5 border-b border-border/30 last:border-0"
                >
                  {/* Timeline dot */}
                  <div className="flex flex-col items-center">
                    <div
                      className={cn(
                        'h-2.5 w-2.5 rounded-full',
                        index === 0
                          ? 'bg-primary'
                          : 'bg-muted-foreground/30'
                      )}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <StatusBadge
                        status={entry.status}
                        variant={getGenericStatusVariant(entry.status)}
                      />
                      <span className="text-[10px] text-muted-foreground font-mono">#{entry.id}</span>
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                      {formatDateTime(entry.createdAt)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </SectionCard>
  );
};

// ============================================================================
// Background Check Section
// ============================================================================

interface BackgroundCheckSectionProps {
  data: import('../../types/userTabs').BackgroundVerificationData | null;
  loading: boolean;
  error: string | null;
}

const BackgroundCheckSection: React.FC<BackgroundCheckSectionProps> = ({ data, loading, error }) => {
  if (loading) {
    return (
      <SectionCard title="Background Check (Checkr)" icon={FileSearch}>
        <div className="flex items-center justify-center py-10">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          <span className="ml-2.5 text-sm text-muted-foreground">Loading...</span>
        </div>
      </SectionCard>
    );
  }

  if (error) {
    const is404 = error.includes('404') || error.toLowerCase().includes('not yet available');
    return (
      <SectionCard title="Background Check (Checkr)" icon={FileSearch}>
        <div className="flex flex-col items-center justify-center py-10 gap-2">
          {is404 ? (
            <>
              <FileSearch className="h-6 w-6 text-muted-foreground/40" />
              <p className="text-sm text-muted-foreground">Endpoint not yet available on the backend</p>
            </>
          ) : (
            <>
              <XCircle className="h-6 w-6 text-red-400" />
              <p className="text-sm text-muted-foreground">{error}</p>
            </>
          )}
        </div>
      </SectionCard>
    );
  }

  if (!data) {
    return (
      <SectionCard title="Background Check (Checkr)" icon={FileSearch}>
        <div className="flex flex-col items-center justify-center py-10 gap-2">
          <FileSearch className="h-6 w-6 text-muted-foreground/40" />
          <p className="text-sm text-muted-foreground">No background check on file</p>
        </div>
      </SectionCard>
    );
  }

  const formatCents = (cents: number) => `$${(cents / 100).toFixed(2)}`;

  return (
    <SectionCard
      title="Background Check (Checkr)"
      icon={FileSearch}
      subtitle={data.status ? `Status: ${data.status}` : undefined}
      actions={
        data.status ? (
          <StatusBadge
            status={data.status}
            variant={getGenericStatusVariant(data.status)}
          />
        ) : undefined
      }
    >
      <div className="space-y-0">
        <InfoRow
          label="Status"
          icon={ShieldCheck}
          value={
            data.status ? (
              <StatusBadge status={data.status} variant={getGenericStatusVariant(data.status)} />
            ) : (
              'N/A'
            )
          }
        />
        <InfoRow
          label="Adjudication"
          icon={CheckCircle2}
          value={
            data.adjudication ? (
              <StatusBadge
                status={data.adjudication}
                variant={getGenericStatusVariant(data.adjudication)}
              />
            ) : (
              'N/A'
            )
          }
        />
        <InfoRow
          label="Report Status"
          icon={FileSearch}
          value={
            data.report_status ? (
              <StatusBadge
                status={data.report_status}
                variant={getGenericStatusVariant(data.report_status)}
              />
            ) : (
              'N/A'
            )
          }
        />
        <InfoRow
          label="Package"
          icon={Package}
          value={data.package}
        />
        <InfoRow
          label="Workflow Stage"
          value={
            data.workflowStage ? (
              <StatusBadge
                status={data.workflowStage.replace(/_/g, ' ')}
                variant={getGenericStatusVariant(data.workflowStage)}
              />
            ) : (
              'N/A'
            )
          }
        />
        {data.recommendedStatusLabel && (
          <InfoRow
            label="Recommended Status"
            value={data.recommendedStatusLabel}
          />
        )}

        {/* Payment */}
        <div className="mt-3 pt-3 border-t border-border/30">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
            Payment
          </p>
          <InfoRow
            label="Payment Status"
            icon={CreditCard}
            value={
              data.paymentStatus ? (
                <StatusBadge
                  status={data.paymentStatus}
                  variant={getGenericStatusVariant(data.paymentStatus)}
                />
              ) : (
                'N/A'
              )
            }
          />
          <InfoRow
            label="Amount"
            icon={DollarSign}
            value={data.paymentAmount != null ? formatCents(data.paymentAmount) : 'N/A'}
          />
        </div>

        {/* Checkr IDs */}
        <div className="mt-3 pt-3 border-t border-border/30">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
            Checkr Identifiers
          </p>
          <InfoRow label="Candidate ID" value={data.candidate_id} mono />
          <InfoRow label="Report ID" value={data.report_id} mono />
        </div>

        {/* Timeline */}
        <div className="mt-3 pt-3 border-t border-border/30">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
            Timeline
          </p>
          <InfoRow
            label="Invited"
            icon={Clock}
            value={data.invitedAt ? formatDateTime(data.invitedAt) : 'N/A'}
          />
          <InfoRow
            label="Completed"
            icon={CheckCircle2}
            value={data.completedAt ? formatDateTime(data.completedAt) : 'N/A'}
          />
          {data.approvedAt && (
            <InfoRow
              label="Approved"
              icon={CheckCircle2}
              value={formatDateTime(data.approvedAt)}
            />
          )}
          {data.deniedAt && (
            <InfoRow
              label="Denied"
              icon={XCircle}
              value={formatDateTime(data.deniedAt)}
            />
          )}
          <InfoRow
            label="Created"
            icon={Clock}
            value={data.createdAt ? formatDateTime(data.createdAt) : 'N/A'}
          />
          <InfoRow
            label="Last Updated"
            icon={Clock}
            value={data.updatedAt ? formatDateTime(data.updatedAt) : 'N/A'}
          />
        </div>
      </div>
    </SectionCard>
  );
};

export default VerificationTab;
